// @ts-check
// Selector de ejercicios de un bloque (TSK-0116, evolucionado a desplegable
// accesible en TSK-0147, MOD-FRONTEND-CORE, CAP-PROGRESS-EXERCISE-SELECTOR,
// docs/03-frontend.md §4 "components no llaman fetch", §13
// accesibilidad/teclado/foco, DEC-060). Componente PURO: no carga datos por
// sí mismo -- los recibe ya cargados vía `mostrarLista(items)` desde la capa
// pages/state (`services/progress-client.js`). No importa nada de
// services/**, state/** ni features/**, no llama a `fetch` ni a
// `window.location`, y no registra ningún listener global: solo el listener
// `change` propio del `<select>` que crea, retirado en `destruir()` junto
// con el elemento.
//
// DEC-060: en vez de una lista `<ol>` de botones siempre visible, el
// componente renderiza un `<select>` nativo (cerrado por defecto, no
// invasivo) con `<optgroup>` por nivel. Solo los ítems desbloqueados o
// completados son opciones seleccionables; los bloqueados NO aparecen como
// opción (pero cuentan en el denominador del contador "X/N desbloqueados").
// El orden recibido del servidor NUNCA se reordena aquí (DEC-059/DEC-060) --
// a diferencia de la versión anterior de este componente, que reintroducía
// el bug ya corregido server-side con un `.sort((a,b)=>a.orden-b.orden)`.
//
// El typedef `EjercicioConEstado` se referencia SOLO como tipo JSDoc (import
// de tipo, se borra en compilación) -- no hay ningún import ejecutable de
// services/** en este archivo.
import { createSafeElement } from '../security/safe-dom.js';
import { crearPanel } from './base/panel.js';
import { crearBarraSegmentos } from './base/barra-segmentos.js';
import './base/cifras.css';
import './exercise-selector.css';

/** @typedef {import('../services/progress-client.js').EjercicioConEstado} EjercicioConEstado */
/** @typedef {import('../services/progress-client.js').NivelProgreso} NivelProgreso */

/**
 * @typedef {Object} OpcionesSelectorEjercicios
 * @property {(slug: string) => void} onSeleccionar - Invocado solo cuando se elige (evento `change`) un ejercicio desbloqueado o completado.
 * @property {string} [tituloSeccion] - Texto del encabezado (por defecto "Ejercicios").
 * @property {boolean} [progresoAparte] - Rediseño (Fase 4b), el HUD de los
 *   editores: el selector se pinta como panel (`ui-panel`) y el contador
 *   «X/N desbloqueados» sale a su propio panel «Progreso», con la cifra en
 *   grande y la barra de N segmentos, que se devuelve en `progreso` para que
 *   la página lo coloque. El texto del contador no cambia. Por defecto
 *   `false`: el contador sigue junto al desplegable, como siempre.
 * @property {HTMLElement} [cabecera] - Rediseño (Fase 4b): lo que la página
 *   pone encima del título, dentro del panel (la etiqueta del editor y el
 *   enlace a su guía). Este componente no lo interpreta, solo lo coloca.
 */

/**
 * Nombres legibles de cada nivel, usados como `label` del `<optgroup>`
 * correspondiente. Un `nivel` desconocido (no debería ocurrir --
 * `progress-client.js` siempre normaliza a uno de los tres valores válidos,
 * ver `normalizarNivel`) cae con gracia al propio valor crudo en vez de
 * reventar el render.
 * @type {Record<NivelProgreso, string>}
 */
const ETIQUETA_NIVEL = {
  inicial: 'Inicial',
  intermedio: 'Intermedio',
  avanzado: 'Avanzado',
};

/**
 * @param {NivelProgreso | string} nivel
 * @returns {string}
 */
function etiquetaNivel(nivel) {
  return ETIQUETA_NIVEL[/** @type {NivelProgreso} */ (nivel)] ?? String(nivel);
}

/**
 * Construye el texto visible de una `<option>`: título + XP máximo (omitido
 * con gracia si `puntuacionXp` es null) + refuerzo TEXTUAL del estado
 * "completado" (WCAG 2.2 SC 1.4.1: el estado nunca se comunica solo con
 * color, y una `<option>` de un `<select>` nativo no admite nodos hijos con
 * icono/color propios, así que el refuerzo aquí es puramente textual).
 * @param {EjercicioConEstado} item
 * @returns {string}
 */
function textoOpcion(item) {
  const { ejercicio, estado } = item;
  const partes = [ejercicio.titulo];
  if (typeof ejercicio.puntuacionXp === 'number') {
    partes.push(`— ${ejercicio.puntuacionXp} XP`);
  }
  let texto = partes.join(' ');
  if (estado === 'completado') {
    texto += item.experienciaRetiradaMotivo ? ' (completado, sin experiencia)' : ' (completado)';
  } else if (estado === 'no_superado') {
    texto += ' (no superado)';
  }
  return texto;
}

/**
 * Crea la `<option>` placeholder ("Elige un ejercicio…"), deshabilitada y
 * seleccionada por defecto: nunca autoselecciona un ejercicio real ni
 * dispara `onSeleccionar` en el render inicial.
 * @returns {HTMLOptionElement}
 */
function crearOpcionPlaceholder() {
  const placeholder = /** @type {HTMLOptionElement} */ (
    createSafeElement('option', { text: 'Elige un ejercicio…' })
  );
  placeholder.value = '';
  placeholder.disabled = true;
  placeholder.selected = true;
  return placeholder;
}

/**
 * Agrupa una secuencia de ítems (ya filtrada a solo desbloqueados/
 * completados) en bloques contiguos del mismo `nivel`, preservando el orden
 * recibido. Dos ítems del mismo nivel separados por uno de nivel distinto
 * generan `<optgroup>` independientes -- deliberado: refleja fielmente la
 * secuencia servida, sin reordenar ni fusionar por nivel "a distancia".
 * @param {EjercicioConEstado[]} items
 * @returns {{ nivel: NivelProgreso, items: EjercicioConEstado[] }[]}
 */
function agruparPorNivelContiguo(items) {
  /** @type {{ nivel: NivelProgreso, items: EjercicioConEstado[] }[]} */
  const grupos = [];
  for (const item of items) {
    const nivel = /** @type {NivelProgreso} */ (item.nivel ?? 'inicial');
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.nivel === nivel) {
      ultimo.items.push(item);
    } else {
      grupos.push({ nivel, items: [item] });
    }
  }
  return grupos;
}

/**
 * Crea el selector de ejercicios de un bloque: un `<section>` con encabezado
 * propio, una región de estado `aria-live="polite"` (anuncia
 * cargando/error/nº de ejercicios disponibles) y un `<select>` nativo (con
 * `<optgroup>` por nivel) más un contador "X/N desbloqueados". Puramente
 * presentacional -- no llama a `fetch`, no importa `services/**`/`state/**`/
 * `features/**`, no navega directamente.
 * @param {OpcionesSelectorEjercicios} opciones
 * @returns {{ elemento: HTMLElement, progreso: HTMLElement | null, mostrarCargando: () => void, mostrarLista: (items: EjercicioConEstado[]) => void, mostrarAviso: (texto: string | null) => void, mostrarError: (mensaje: string) => void, seleccionar: (slug: string) => boolean, marcarSeleccionado: (slug: string) => boolean, destruir: () => void }}
 */
export function crearSelectorEjercicios(opciones) {
  const { onSeleccionar, tituloSeccion = 'Ejercicios', progresoAparte = false, cabecera } = opciones;

  const sufijoId = Math.random().toString(36).slice(2);
  const idEncabezado = `exercise-selector-titulo-${sufijoId}`;
  const idSelect = `exercise-selector-select-${sufijoId}`;

  const seccion = createSafeElement('section', {
    className: progresoAparte ? 'exercise-selector ui-panel' : 'exercise-selector',
  });
  seccion.setAttribute('aria-labelledby', idEncabezado);

  const encabezado = createSafeElement('h2', {
    className: progresoAparte
      ? 'exercise-selector-encabezado ui-panel-titulo'
      : 'exercise-selector-encabezado',
    text: tituloSeccion,
  });
  encabezado.id = idEncabezado;

  const estadoRegion = createSafeElement('p', { className: 'exercise-selector-estado-region' });
  estadoRegion.setAttribute('aria-live', 'polite');

  const controles = createSafeElement('div', { className: 'exercise-selector-controles' });

  const label = createSafeElement('label', {
    className: 'exercise-selector-label',
    text: 'Selecciona un ejercicio',
  });
  label.setAttribute('for', idSelect);

  const select = /** @type {HTMLSelectElement} */ (
    createSafeElement('select', { className: 'exercise-selector-select' })
  );
  select.id = idSelect;

  const contador = createSafeElement('p', { className: 'exercise-selector-contador' });

  // Por qué hay ejercicios bloqueados, cuando no basta con el orden (24 sep
  // 2026: un bloque espera a que se termine el anterior). Texto normal, no
  // región viva: es información estable que se lee con el resto de la
  // tarjeta, y el recuento de disponibles ya se anuncia en `estadoRegion`.
  const aviso = createSafeElement('p', { className: 'exercise-selector-aviso' });
  aviso.hidden = true;

  function ocultarAviso() {
    aviso.textContent = '';
    aviso.hidden = true;
  }

  // La experiencia retirada por el profesor (DEC-182): al elegir un ejercicio al
  // que se la han quitado, se dice AQUÍ con el motivo que escribió. Texto normal
  // (no región viva): es lo que se lee al elegirlo, y el ejercicio sigue hecho.
  const avisoExperiencia = createSafeElement('p', {
    className: 'exercise-selector-experiencia',
    attrs: { role: 'note' },
  });
  avisoExperiencia.hidden = true;
  /** @type {Map<string, string>} slug -> motivo */
  const motivosRetirada = new Map();

  function pintarAvisoExperiencia() {
    const motivo = motivosRetirada.get(select.value);
    avisoExperiencia.textContent = motivo
      ? `Tu profesor te ha retirado la experiencia de este ejercicio. Motivo: ${motivo} ` +
        'El ejercicio sigue contando como hecho.'
      : '';
    avisoExperiencia.hidden = !motivo;
  }

  // Rediseño (Fase 4b): con `progresoAparte`, el contador vive en su propio
  // panel «Progreso» junto a la barra de N segmentos (decorativa: lo que se
  // lee es el contador). N sale de la lista, nunca se escribe a mano.
  /** @type {HTMLElement | null} */
  let progreso = null;
  /** @type {ReturnType<typeof crearBarraSegmentos> | null} */
  let barra = null;
  if (progresoAparte) {
    const panel = crearPanel({
      titulo: 'Progreso',
      tituloComoEtiqueta: true,
      clase: 'exercise-selector-progreso',
    });
    barra = crearBarraSegmentos({ total: 0, encendidos: 0 });
    contador.classList.add('ui-cifra-valor');
    const cifra = createSafeElement('div', { className: 'ui-cifra ui-cifra-destacada' });
    cifra.append(contador);
    panel.cuerpo.append(cifra, barra.elemento);
    progreso = panel.elemento;
    controles.append(label, select);
  } else {
    controles.append(label, select, contador);
  }
  seccion.append(
    ...(cabecera ? [cabecera] : []),
    encabezado,
    estadoRegion,
    controles,
    aviso,
    avisoExperiencia,
  );

  /**
   * La última lista pintada, para los momentos de recompensa (Fase 6b).
   * @type {{ desbloqueados: number, total: number } | null}
   */
  let ultimaLista = null;

  /**
   * El momento de recompensa del HUD (plan de la Fase 3, §3), con la señal
   * que ya existe: al acertar, la página RECARGA la lista y el contador
   * crece. Un nivel nuevo: su segmento sube («sube»); todos abiertos: ola.
   * La primera carga, o una recarga sin cambios, no celebra nada.
   * @param {number} desbloqueados
   * @param {number} total
   * @returns {'sube' | 'ola' | null}
   */
  function momentoDe(desbloqueados, total) {
    const anterior = ultimaLista;
    ultimaLista = { desbloqueados, total };
    if (!anterior || anterior.total !== total || desbloqueados <= anterior.desbloqueados) return null;
    return desbloqueados === total ? 'ola' : 'sube';
  }

  /**
   * «X/N desbloqueados». En el HUD, la X va aparte para pintarla en grande
   * (y en su propio `span`, para que se mueva en su momento); el texto que se
   * lee es el mismo.
   * @param {number} desbloqueados
   * @param {number} total
   */
  function pintarContador(desbloqueados, total) {
    const momento = momentoDe(desbloqueados, total);
    if (!barra) {
      contador.textContent = `${desbloqueados}/${total} desbloqueados`;
      return;
    }
    contador.replaceChildren(
      createSafeElement('span', { className: 'exercise-selector-numero', text: String(desbloqueados) }),
      createSafeElement('span', { className: 'ui-cifra-unidad', text: `/${total} desbloqueados` }),
    );
    barra.actualizar({
      total,
      encendidos: desbloqueados,
      recien: momento ? desbloqueados - 1 : null,
      momento,
    });
    if (!progreso) return;
    if (momento) progreso.dataset.momento = momento;
    else delete progreso.dataset.momento;
  }

  function vaciarContador() {
    contador.textContent = '';
    barra?.actualizar({ total: 0, encendidos: 0 });
    if (progreso) delete progreso.dataset.momento;
  }

  /**
   * Limpia el estado de error (`role="alert"`) de la región aria-live antes
   * de escribir un mensaje distinto.
   */
  function limpiarRegionEstado() {
    estadoRegion.removeAttribute('role');
    estadoRegion.textContent = '';
  }

  /**
   * Deja el `<select>` con únicamente la opción placeholder, deshabilitada y
   * seleccionada -- usado por `mostrarCargando`/`mostrarError` y por el caso
   * vacío de `mostrarLista` (0 desbloqueados).
   * @param {boolean} deshabilitado
   */
  function reiniciarSelect(deshabilitado) {
    select.replaceChildren(crearOpcionPlaceholder());
    select.disabled = deshabilitado;
  }

  /** @param {(value: Event) => void} handler */
  function alCambiar(handler) {
    select.addEventListener('change', handler);
    return () => select.removeEventListener('change', handler);
  }

  const quitarListenerChange = alCambiar(() => {
    pintarAvisoExperiencia();
    if (!select.value) return; // placeholder: nunca invoca onSeleccionar
    onSeleccionar(select.value);
  });

  reiniciarSelect(true);

  return {
    elemento: seccion,
    progreso,

    mostrarCargando() {
      limpiarRegionEstado();
      ocultarAviso();
      motivosRetirada.clear();
      pintarAvisoExperiencia();
      reiniciarSelect(true);
      vaciarContador();
      estadoRegion.textContent = 'Cargando ejercicios…';
    },

    mostrarLista(items) {
      limpiarRegionEstado();

      const visibles = items.filter((item) => item.estado !== 'bloqueado');
      motivosRetirada.clear();
      for (const item of visibles) {
        if (item.experienciaRetiradaMotivo) {
          motivosRetirada.set(item.ejercicio.slug, item.experienciaRetiradaMotivo);
        }
      }

      if (visibles.length === 0) {
        select.replaceChildren(crearOpcionPlaceholder());
        select.disabled = true;
      } else {
        const grupos = agruparPorNivelContiguo(visibles);
        const nodosGrupos = grupos.map((grupo) => {
          const optgroup = /** @type {HTMLOptGroupElement} */ (
            createSafeElement('optgroup')
          );
          optgroup.label = etiquetaNivel(grupo.nivel);
          const opciones = grupo.items.map((item) => {
            const option = /** @type {HTMLOptionElement} */ (createSafeElement('option'));
            option.value = item.ejercicio.slug;
            option.textContent = textoOpcion(item);
            return option;
          });
          optgroup.append(...opciones);
          return optgroup;
        });
        select.replaceChildren(crearOpcionPlaceholder(), ...nodosGrupos);
        select.disabled = false;
      }

      pintarContador(visibles.length, items.length);
      pintarAvisoExperiencia();

      const cantidad = visibles.length;
      estadoRegion.textContent =
        cantidad === 1 ? '1 ejercicio disponible' : `${cantidad} ejercicios disponibles`;
    },

    /**
     * Pone (o quita, con `null`) el aviso de por qué hay ejercicios
     * bloqueados. La página decide el texto; este componente solo lo pinta.
     * @param {string | null} texto
     */
    mostrarAviso(texto) {
      aviso.textContent = texto ?? '';
      aviso.hidden = !texto;
    },

    mostrarError(mensaje) {
      reiniciarSelect(true);
      // El aviso describía una lista que ya no está en pantalla.
      ocultarAviso();
      motivosRetirada.clear();
      pintarAvisoExperiencia();
      vaciarContador();
      estadoRegion.setAttribute('role', 'alert');
      estadoRegion.textContent = mensaje;
    },

    /**
     * Selecciona programáticamente el ejercicio `slug` si existe como opción
     * seleccionable (desbloqueado/completado) en la lista ya renderizada, e
     * invoca `onSeleccionar(slug)` -- el mismo camino que un cambio manual, para
     * reutilizar la carga de la página. Usado para restaurar el ejercicio
     * seleccionado tras un refresco (persistencia en la capa `pages`). Devuelve
     * `true` si el `slug` existía y se seleccionó; `false` en caso contrario
     * (p.ej. un slug guardado que ya no está desbloqueado).
     * @param {string} slug
     * @returns {boolean}
     */
    seleccionar(slug) {
      if (!slug) return false;
      const existe = Array.from(select.options).some((opcion) => opcion.value === slug);
      if (!existe) return false;
      select.value = slug;
      if (select.value !== slug) return false;
      pintarAvisoExperiencia();
      onSeleccionar(slug);
      return true;
    },

    /**
     * Marca VISUALMENTE `slug` como la opción seleccionada del `<select>` sin
     * invocar `onSeleccionar` (a diferencia de `seleccionar`). Para páginas que
     * cargan el ejercicio por otra vía (p.ej. el editor de diagramas, cuyo
     * `onSeleccionar` NAVEGA con recarga completa): tras restaurar el ejercicio
     * desde la persistencia, sincroniza el desplegable para que muestre cuál se
     * está trabajando. Devuelve `true` si el `slug` existía como opción.
     * @param {string} slug
     * @returns {boolean}
     */
    marcarSeleccionado(slug) {
      if (!slug) return false;
      const existe = Array.from(select.options).some((opcion) => opcion.value === slug);
      if (!existe) return false;
      select.value = slug;
      pintarAvisoExperiencia();
      return select.value === slug;
    },

    destruir() {
      quitarListenerChange();
      seccion.remove();
      progreso?.remove();
    },
  };
}
